import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { isEqual } from 'lodash-es';

import { PageMetadata } from '@/features/pages/types/page.types';
import pageRepository, { getPageMetadataProps } from '@/features/pages/repositories/PageRepository';
import collectionRepository, { getSectionProperties } from '../repositories/CollectionRepository';
import { useCollectionStore } from '../stores/useCollectionStore';
import {
  Collection,
  DisplayedCollectionSection,
  CollectionFilter,
} from '../types/collection.types';

// Define the type with all collection-related actions
export interface CollectionsContextType {
  loadCollections: () => Promise<void>;
  addCollection: () => Promise<Collection>;
  archiveCollection: (coll: Collection) => Promise<void>;
  unarchiveCollection: (coll: Collection) => Promise<void>;
  searchCollections: (query: string) => Promise<Collection[]>;
  updateCollection: (id: string, collection: Partial<Collection>) => Promise<void>;
  setCollection: (collectionId: string | null) => Promise<Collection | null>;
  loadCollectionStructure: (collectionId?: string) => Promise<DisplayedCollectionSection[]>;
  updateStructureItem: (newStructure: DisplayedCollectionSection[]) => Promise<void>;
  archiveCollectionSection: (sectionId: string) => Promise<void>;
  toggleProperty: (id: string, property: 'isFavourite' | 'isPublic') => Promise<void>;
  getCollections: (collectionFilter: CollectionFilter, tagFilter?: string) => Promise<Collection[]>;
}

const CollectionsContext = createContext<CollectionsContextType | null>(null);

export const CollectionsProvider = ({ children }: { children: React.ReactNode }) => {
  // Get state and setters from stores using selectors
  const collections = useCollectionStore(state => state.collections);
  const setCollections = useCollectionStore(state => state.setCollections);
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const setCurrentCollection = useCollectionStore(state => state.setCurrentCollection);
  const collectionStructure = useCollectionStore(state => state.collectionStructure);
  const setCollectionStructure = useCollectionStore(state => state.setCollectionStructure);
  const setCurrentCollections = useCollectionStore(state => state.setCurrentCollections);
  const updateCollectionInStore = useCollectionStore(state => state.updateCollection);
  const resetCurrentCollection = useCollectionStore(state => state.resetCurrentCollection);
  const setLoading = useCollectionStore(state => state.setLoading);

  const loadCollections = useCallback(async () => {
    setLoading(true);
    const collections = await collectionRepository.getCollections();
    setCollections(collections);
    setCurrentCollections(collections);
    setLoading(false);
  }, [setCollections, setCurrentCollections, setLoading]);

  const addCollection = useCallback(async () => {
    const coll = await collectionRepository.addCollection();
    setCollections([coll, ...collections]);
    return coll;
  }, [collections, setCollections]);

  const archiveCollection = useCallback(
    async (coll: Collection) => {
      await collectionRepository.archiveCollectionData(coll);
      await loadCollections();
    },
    [loadCollections]
  );

  const unarchiveCollection = useCallback(
    async (coll: Collection) => {
      if (!coll.archivedAt) return;

      await collectionRepository.unarchiveCollectionData(coll);

      await loadCollections();
    },
    [loadCollections]
  );

  const searchCollections = useCallback(
    async (query: string) => {
      const results = await collectionRepository.searchCollections(query);
      setCollections(results);
      return results;
    },
    [setCollections]
  );

  const updateCollection = useCallback(
    async (id: string, collection: Partial<Collection>) => {
      await collectionRepository.updateCollection(id, collection);
      updateCollectionInStore(id, collection);
    },
    [updateCollectionInStore]
  );

  const loadCollectionStructure = useCallback(
    async (collectionId?: string) => {
      if (!collectionId) {
        setCollectionStructure([]);
        return [];
      }

      const collection = await collectionRepository.getCollectionById(collectionId);
      if (!collection) {
        setCollectionStructure([]);
        return [];
      }

      const collectionPageMetadata =
        await collectionRepository.getCollectionPageMetadata(collectionId);

      const structure: DisplayedCollectionSection[] = (collection.sections || [])
        .filter(s => !s.archivedAt)
        .map(section => ({
          section: {
            ...section,
            pages: collectionPageMetadata
              .filter(p => p.sectionId === section.id)
              .map(getPageMetadataProps),
          },
        }));

      setCollectionStructure(structure);
      return structure;
    },
    [setCollectionStructure]
  );

  const setCollection = useCallback(
    async (collectionId: string | null) => {
      if (!collectionId) {
        resetCurrentCollection();
        return null;
      }

      setLoading(true);

      const collection = await collectionRepository.getCollectionById(collectionId);
      if (!collection) {
        resetCurrentCollection();
        setLoading(false);
        return null;
      }
      const sections = (collection.sections || []).map(getSectionProperties);
      const updatedCollection = { ...collection, sections };
      setCurrentCollection(updatedCollection);
      const structure = await loadCollectionStructure(collection.id);
      setLoading(false);
      return { ...updatedCollection, structure };
    },
    [loadCollectionStructure, resetCurrentCollection, setCurrentCollection, setLoading]
  );

  const updateStructureItem = useCallback(
    async (newStructure: DisplayedCollectionSection[]) => {
      if (!currentCollection) return;

      const newSections = newStructure.map(s => s.section);
      const oldSections = currentCollection.sections || [];
      const hasSectionsChanged = !isEqual(newSections, oldSections);

      if (hasSectionsChanged) {
        await collectionRepository.updateCollection(currentCollection.id, {
          sections: [
            ...newSections,
            ...oldSections
              .filter(os => !newSections.map(nS => nS.id).includes(os.id))
              .map(os => ({ ...os, archivedAt: new Date().getTime() })),
          ],
        });

        setCurrentCollection({
          ...currentCollection,
          sections: newSections,
        });
      }

      setCollectionStructure(newStructure);
      const allPages = newStructure.map(s => s.section.pages).flat();
      const pagesMap = new Map<string, PageMetadata>();
      collectionStructure.forEach(s => {
        s.section.pages.forEach(page => pagesMap.set(page.id, page));
      });

      // Update page metadata changes
      for (const page of allPages) {
        const oldPage = pagesMap.get(page.id);
        if (isEqual(oldPage, page)) continue;
        await pageRepository.updatePage(page.id, {
          sectionId: page.sectionId,
          orderNumber: page.orderNumber,
        });
      }

      void loadCollectionStructure(currentCollection.id);
    },
    [
      collectionStructure,
      currentCollection,
      loadCollectionStructure,
      setCollectionStructure,
      setCurrentCollection,
    ]
  );

  const archiveCollectionSection = useCallback(
    async (sectionId: string) => {
      if (!currentCollection) return;

      await collectionRepository.archiveCollectionData(currentCollection, sectionId);
      await loadCollectionStructure(currentCollection.id);
    },
    [currentCollection, loadCollectionStructure]
  );

  const toggleProperty = useCallback(
    async (id: string, property: 'isFavourite' | 'isPublic') => {
      await collectionRepository.toggleProperty(id, property);

      // Update the current collection if it's the one being toggled
      if (currentCollection?.id === id) {
        updateCollectionInStore(id, {
          isFavourite: !currentCollection[property],
        });
      }

      // Reload collections to reflect the updated order
      await loadCollections();
    },
    [currentCollection, updateCollectionInStore, loadCollections]
  );

  const getCollections = useCallback((collectionFilter: CollectionFilter, tagFilter?: string) => {
    switch (collectionFilter) {
      case CollectionFilter.All:
        return collectionRepository.getCollections();
      case CollectionFilter.Favourites:
        return collectionRepository.getFavouritedCollections();
      case CollectionFilter.Archived: {
        const archived = true;
        return collectionRepository.getCollections(archived);
      }
      case CollectionFilter.Public:
        return collectionRepository.getPublicCollections();
      case CollectionFilter.Tagged:
        if (!tagFilter) return collectionRepository.getCollections();
        return collectionRepository.searchTaggedCollections(tagFilter);
    }
  }, []);

  // Memoize the context value to prevent unnecessary rerenders
  const value = useMemo(
    () => ({
      loadCollections,
      addCollection,
      archiveCollection,
      unarchiveCollection,
      searchCollections,
      updateCollection,
      setCollection,
      loadCollectionStructure,
      updateStructureItem,
      archiveCollectionSection,
      toggleProperty,
      getCollections,
    }),
    [
      loadCollections,
      addCollection,
      archiveCollection,
      unarchiveCollection,
      searchCollections,
      updateCollection,
      setCollection,
      loadCollectionStructure,
      updateStructureItem,
      archiveCollectionSection,
      toggleProperty,
      getCollections,
    ]
  );

  return <CollectionsContext.Provider value={value}>{children}</CollectionsContext.Provider>;
};

// Custom hook to use the context
export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }
  return context;
};
